import { CaseAdmin, CaseSlideAdmin, PatientInsurance, SlideAdmin } from "pwapi";

import { SlideUrls } from "./adminSlideReducer";
import {
  ADMIN_UPDATE_CASE_FAILURE,
  ADMIN_UPDATE_CASE_REQUEST,
  ADMIN_UPDATE_CASE_SUCCESS,
  ADMIN_CASE_SUCCESS,
  ADMIN_UPDATE_PATIENT_FAILURE,
  ADMIN_UPDATE_PATIENT_REQUEST,
  ADMIN_UPDATE_PATIENT_SUCCESS,
  ADMIN_UPDATE_SLIDE_SUCCESS,
  ADMIN_UPDATE_SPECIMEN_SUCCESS,
  ADMIN_DELETE_SLIDE_SUCCESS,
  ADMIN_UPDATE_SLIDE_CASES_FAILURE,
  ADMIN_UPDATE_SLIDE_REQUEST,
  ADMIN_DELETE_CASE_SUCCESS,
  ADMIN_DELETE_SPECIMEN_SUCCESS,
  ADMIN_CREATE_SPECIMEN_SUCCESS,
  ADMIN_RESET_ERROR_MESSAGE,
  ADMIN_DELETE_CASE_FAILURE,
  ADMIN_RESET_DELETION_STATUS,
  ADMIN_DELETE_CASE_REQUEST,
  ADMIN_CASE_REQUEST,
  ADMIN_UPDATE_CASE_DOCUMENT_SUCCESS,
  ADMIN_DELETE_CASE_DOCUMENT_SUCCESS,
  ADMIN_SLIDE_BARCODE_SET,
  ADMIN_SLIDE_MACRO_SET,
  ADMIN_UPLOAD_CASE_DOCUMENT_SUCCESS,
  ADMIN_UPDATE_INSURANCE_SUCCESS,
  ADMIN_CREATE_INSURANCE_SUCCESS,
  ADMIN_UPDATE_PATIENT_LOCATION_SUCCESS,
  ADMIN_DELETE_INSURANCE_SUCCESS,
  ADMIN_CREATE_QAREVIEW_SUCCESS,
  ADMIN_UPDATE_INTEGRATIONS_SUCCESS,
  ADMIN_UPDATE_INTEGRATIONS_REQUEST,
  ADMIN_UPDATE_INTEGRATIONS_FAILURE,
  ADMIN_UPDATE_PATIENT_GUARANTOR_SUCCESS,
  ADMIN_CREATE_PATIENT_GUARANTOR_SUCCESS,
  ADMIN_REASSIGN_ACCESSION_NUMBER_REQUEST,
  ADMIN_REASSIGN_ACCESSION_NUMBER_SUCCESS,
  ADMIN_REASSIGN_ACCESSION_NUMBER_FAILURE,
  ADMIN_CREATE_BLOCK_SUCCESS,
  ADMIN_CREATE_SLIDE_SUCCESS,
  ADMIN_UPDATE_BLOCK_SUCCESS,
  ADMIN_DELETE_BLOCK_SUCCESS,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_SUCCESS,
  ADMIN_ORDER_PRINT_SLIDE_SUCCESS,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_SUCCESS,
  ADMIN_HOLD_CASE_DATA_ENTRY_SUCCESS,
} from "../../actions/adminConstants";
import {
  CASE_REQUEST_SCREEN_SUCCESS,
  CASE_UNDO_SCREEN_SUCCESS,
  COMPLETE_STAIN_ORDER_SUCCESS,
  DELETE_STAIN_ORDER_SUCCESS,
  STAIN_ORDER_SUCCESS,
} from "../../actions/constants";
import { Modify } from "../caseDetailReducer";

export type CaseSlideAdminWithUrls = CaseSlideAdmin & SlideUrls;

export interface CaseAdminDetail
  extends Modify<
    CaseAdmin,
    {
      slides: CaseSlideAdminWithUrls[];
      isCaseSaving?: boolean;
      isPatientSaving?: boolean;
      isIntegrationSaving?: boolean;
      delErrorMessage?: string;
      delStatus?: "loading" | "success";
      loading: false;
      remains_digital_pc?: boolean;
      indigent?: boolean;
      self_pay_with_attestation?: boolean;
      finalized_at?: string;
    }
  > {}

const reducer = (
  state: CaseAdminDetail | null = null,
  action: any
): CaseAdminDetail | null => {
  switch (action.type) {
    case ADMIN_UPDATE_CASE_REQUEST:
    case ADMIN_REASSIGN_ACCESSION_NUMBER_REQUEST:
      return state
        ? {
            ...state,
            isCaseSaving: true,
          }
        : state;
    case CASE_REQUEST_SCREEN_SUCCESS:
    case CASE_UNDO_SCREEN_SUCCESS:
      return state
        ? {
            ...state,
            finalize_assignment: action.payload.response.finalize_assignment,
            screen_assignment: action.payload.response.screen_assignment,
            active_assignment: action.payload.response.active_assignment,
            is_screening: action.payload.response.is_screening,
            screen_requests: action.payload.screen_requests,
          }
        : state;
    case ADMIN_DELETE_CASE_SUCCESS:
      return null;
    case ADMIN_DELETE_CASE_FAILURE:
      return (
        state && {
          ...state,
          delErrorMessage: "Case could not be deleted.",
          delStatus: undefined,
        }
      );
    case ADMIN_DELETE_CASE_REQUEST:
      return (
        state && {
          ...state,
          delStatus: "loading",
        }
      );
    case ADMIN_CASE_SUCCESS:
      return {
        ...state,
        ...action.payload.response,
        isCaseSaving: false,
      };
    case ADMIN_UPDATE_CASE_SUCCESS:
      return {
        ...state,
        ...action.payload,
        isCaseSaving: false,
      };
    case ADMIN_REASSIGN_ACCESSION_NUMBER_SUCCESS:
      return {
        ...state,
        ...action.payload.response,
        isCaseSaving: false,
      };
    case ADMIN_COMPLETE_CASE_DATA_ENTRY_SUCCESS:
    case ADMIN_HOLD_CASE_DATA_ENTRY_SUCCESS:
      return (
        state && {
          ...state,
          data_entry: action.payload.response.data_entry
            ? action.payload.response.data_entry
            : state.data_entry,
          data_entries:
            action.payload.response.data_entries || state.data_entries,
        }
      );
    case ADMIN_UPDATE_CASE_FAILURE:
    case ADMIN_REASSIGN_ACCESSION_NUMBER_FAILURE:
      return state
        ? {
            ...state,
            isCaseSaving: false,
          }
        : state;
    case ADMIN_RESET_ERROR_MESSAGE:
      return (
        state && {
          ...state,
          delErrorMessage: undefined,
        }
      );
    case ADMIN_RESET_DELETION_STATUS:
      return (
        state && {
          ...state,
          delStatus: undefined,
        }
      );

    case ADMIN_CREATE_BLOCK_SUCCESS:
      if (!state || !state.blocks) {
        return state;
      } else {
        return {
          ...state,
          blocks: [...state.blocks, action.payload.response],
        };
      }

    case ADMIN_UPDATE_BLOCK_SUCCESS: {
      if (!state || !state.blocks) {
        return state;
      } else {
        return {
          ...state,
          blocks: state.blocks.map((block) => {
            return block.id === action.payload.response.id
              ? {
                  ...block,
                  ...action.payload.response,
                }
              : block;
          }),
        };
      }
    }

    case ADMIN_DELETE_BLOCK_SUCCESS:
      if (!state || !state.blocks) {
        return state;
      } else {
        return {
          ...state,
          blocks: state.blocks.filter((block) => block.id !== action.args.id),
          lab_orders: action.payload?.response?.deleted_objects?.lab_orders
            ?.length
            ? state.lab_orders?.filter(
                (order) =>
                  !action.payload.response.deleted_objects.lab_orders.includes(
                    order.id
                  )
              )
            : state.lab_orders,
        };
      }

    /* Specimen updates */
    case ADMIN_CREATE_SPECIMEN_SUCCESS:
      return state && state.id === action.payload.response.case_id
        ? {
            ...state,
            specimens: state.specimens
              ? [...state.specimens, action.payload.response]
              : [action.payload.response],
          }
        : state;
    case ADMIN_UPDATE_SPECIMEN_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            specimens: state.specimens
              ? state.specimens.map((specimen) => {
                  return specimen.id === action.payload.specimen.id
                    ? {
                        ...specimen,
                        ...action.payload.specimen,
                      }
                    : specimen;
                })
              : state.specimens,
          }
        : state;

    case ADMIN_DELETE_SPECIMEN_SUCCESS:
      return state && state.id === action.args.case_id
        ? {
            ...state,
            specimens: state.specimens?.filter(
              (specimen) => specimen.id !== action.args.id
            ),
          }
        : state;

    /* Patient updates */
    case ADMIN_UPDATE_PATIENT_REQUEST:
      return state
        ? {
            ...state,
            isPatientSaving: true,
          }
        : state;
    case ADMIN_UPDATE_PATIENT_FAILURE:
      return state
        ? {
            ...state,
            isPatientSaving: false,
          }
        : state;
    case ADMIN_UPDATE_PATIENT_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            patient:
              action.payload.patient?.id === state.patient?.id
                ? action.payload.patient
                : state.patient,
            isPatientSaving: false,
          }
        : state;
    case ADMIN_CREATE_INSURANCE_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            patient:
              action.payload.patient_id === state.patient?.id
                ? {
                    ...state.patient,
                    insurance: state.patient.insurance
                      ? [
                          ...(action.payload.insurance.primary_provider
                            ? state.patient.insurance?.map((insur) => ({
                                ...insur,
                                primary_provider: false,
                              }))
                            : state.patient.insurance),
                          action.payload.insurance,
                        ]
                      : [action.payload.insurance],
                  }
                : state.patient,
          }
        : state;
    case ADMIN_UPDATE_INSURANCE_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            patient:
              action.payload.patient_id === state.patient?.id
                ? {
                    ...state.patient,
                    insurance: state.patient.insurance
                      ? state.patient.insurance.map((insur) => {
                          return insur.id === action.payload.insurance.id
                            ? {
                                ...insur,
                                ...action.payload.insurance,
                              }
                            : action.payload.insurance.primary_provider
                            ? { ...insur, primary_provider: false }
                            : insur;
                        }, [])
                      : [action.payload.insurance],
                  }
                : state.patient,
          }
        : state;
    case ADMIN_UPDATE_PATIENT_LOCATION_SUCCESS: {
      return state
        ? {
            ...state,
            patient: state.patient
              ? {
                  ...state.patient,
                  address: state.patient.address
                    ? {
                        ...state.patient.address,
                        ...action.payload,
                      }
                    : action.payload,
                }
              : state.patient,
          }
        : state;
    }
    case ADMIN_CREATE_PATIENT_GUARANTOR_SUCCESS:
    case ADMIN_UPDATE_PATIENT_GUARANTOR_SUCCESS:
      return state
        ? {
            ...state,
            patient: state.patient
              ? {
                  ...state.patient,
                  guarantor: state.patient.guarantor?.[0]
                    ? [{ ...state.patient.guarantor[0], ...action.payload }]
                    : [action.payload],
                }
              : state.patient,
          }
        : state;
    case ADMIN_DELETE_INSURANCE_SUCCESS: {
      return state
        ? {
            ...state,
            patient: state.patient
              ? {
                  ...state.patient,
                  insurance: state.patient.insurance
                    ? state.patient.insurance.reduce(
                        (acc: PatientInsurance[], insur) => {
                          return insur.id === action.payload.id
                            ? acc
                            : [...acc, insur];
                        },
                        []
                      )
                    : [],
                }
              : state.patient,
          }
        : state;
    }
    /* Slide updates and deletions */
    case ADMIN_UPDATE_SLIDE_REQUEST: {
      const slideCase = action.payload.slide.case || action.payload.case;
      return state && state.id === slideCase?.id
        ? {
            ...state,
            slides: state.slides.map((slide: CaseSlideAdminWithUrls) => {
              return slide.id === action.payload.slide.id
                ? {
                    ...slide,
                    isLoading: true,
                  }
                : slide;
            }),
          }
        : state;
    }
    case ADMIN_UPDATE_SLIDE_CASES_FAILURE: {
      const slideCase = action.payload.slide.case || action.payload.case;
      return state && state.id === slideCase?.id
        ? {
            ...state,
            slides: state.slides.map((slide: CaseSlideAdminWithUrls) => {
              return slide.id === action.payload.slide.id
                ? {
                    ...slide,
                    isLoading: false,
                  }
                : slide;
            }),
          }
        : state;
    }
    case ADMIN_CREATE_SLIDE_SUCCESS:
    case ADMIN_ORDER_PRINT_SLIDE_SUCCESS:
      if (
        !state ||
        !action.payload.response.case?.id ||
        state.id !== action.payload.response.case.id
      ) {
        return state;
      } else {
        return {
          ...state,
          slides: [...state.slides, action.payload.response],
        };
      }
    case ADMIN_PRINT_LAB_ORDERS_SLIDES_SUCCESS:
      if (!state) {
        return state;
      }
      return {
        ...state,
        slides: [
          ...state.slides,
          ...action.payload.response?.filter(
            (s: SlideAdmin) => s.case?.id === state.id
          ),
        ],
      };
    case ADMIN_UPDATE_SLIDE_SUCCESS:
      if (!state) {
        return state;
      }

      // Cases aren't the same, so remove the slide if it is in state
      if (state.id !== action.payload.case.id) {
        return {
          ...state,
          slides: state.slides.filter(
            (slide: CaseSlideAdminWithUrls) => slide.id !== action.payload.id
          ),
        };
      } else {
        // Updated slide case and state case are the same so check if
        // the slide is already in the case
        const slideInCase = state.slides.find(
          (slide) => slide.id === action.payload.id
        );
        // Update the slide if already in the case
        if (slideInCase) {
          return {
            ...state,
            status: action.payload.case?.status || state.status,
            slides: state.slides.map((slide) => {
              return slide.id === action.payload.id
                ? {
                    ...slide,
                    ...action.payload,
                    isLoading: false,
                  }
                : slide;
            }),
          };
        } else {
          // Add the slide to the case if it is not in the case already
          return {
            ...state,
            slides: [...state.slides, action.payload],
          };
        }
      }
    case ADMIN_DELETE_SLIDE_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            slides: state.slides.filter((slide: CaseSlideAdminWithUrls) => {
              return slide.id !== action.payload.slide_id;
            }),
          }
        : state;
    case ADMIN_UPDATE_CASE_DOCUMENT_SUCCESS:
      if (!state || !state.documents) {
        return state;
      }

      // Document has been removed from the case
      if (state.id !== action.payload.case_id) {
        return {
          ...state,
          documents: state.documents.filter((document) => {
            return document.id !== action.payload.id;
          }),
        };
      } else {
        // Update the current document in the case
        return {
          ...state,
          documents: state.documents.map((document) => {
            return document.id === action.payload.id
              ? { ...document, ...action.payload }
              : document;
          }),
        };
      }
    case ADMIN_UPLOAD_CASE_DOCUMENT_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            documents: [...(state.documents || []), action.payload],
          }
        : state;
    case ADMIN_DELETE_CASE_DOCUMENT_SUCCESS:
      return state
        ? {
            ...state,
            documents: state.documents?.filter((document: any) => {
              return document.id !== action.payload.id;
            }),
          }
        : state;
    case ADMIN_SLIDE_BARCODE_SET:
      return state
        ? {
            ...state,
            slides: state.slides.map((slide) => {
              return slide.id === action.payload.slideId
                ? {
                    ...slide,
                    barcode_url: action.payload.url,
                  }
                : slide;
            }),
          }
        : state;
    case ADMIN_SLIDE_MACRO_SET:
      return state
        ? {
            ...state,
            slides: state.slides.map((slide) => {
              return slide.id === action.payload.slideId
                ? {
                    ...slide,
                    macro_url: action.payload.url,
                  }
                : slide;
            }),
          }
        : state;
    case ADMIN_CREATE_QAREVIEW_SUCCESS:
      return state
        ? {
            ...state,
            qa_reviews: state.qa_reviews
              ? [...state.qa_reviews, action.payload]
              : [action.payload],
          }
        : state;
    case ADMIN_UPDATE_INTEGRATIONS_REQUEST:
      return state
        ? {
            ...state,
            isIntegrationSaving: true,
          }
        : state;
    case ADMIN_UPDATE_INTEGRATIONS_FAILURE:
      return state
        ? {
            ...state,
            isIntegrationSaving: false,
          }
        : state;
    case ADMIN_UPDATE_INTEGRATIONS_SUCCESS:
      return state
        ? {
            ...state,
            ...action.payload.case,
            patient: state.patient
              ? {
                  ...state.patient,
                  external_patient_id:
                    action.payload.patient?.external_patient_id,
                  clinic: action.payload.clinic,
                }
              : state.patient,
            isIntegrationSaving: false,
          }
        : state;
    case STAIN_ORDER_SUCCESS:
      return state
        ? {
            ...state,
            lab_orders: [...(state.lab_orders || []), action.payload.response],
          }
        : state;
    case COMPLETE_STAIN_ORDER_SUCCESS:
      return (
        state && {
          ...state,
          lab_orders: (state.lab_orders || []).map((lab_order) =>
            lab_order.id !== action.args
              ? lab_order
              : {
                  ...lab_order,
                  completed_at: action.payload.response.completed_at,
                }
          ),
        }
      );
    case DELETE_STAIN_ORDER_SUCCESS:
      return (
        state && {
          ...state,
          lab_orders: (state.lab_orders || []).map((lab_order) =>
            lab_order.id !== action.args.id
              ? lab_order
              : {
                  ...lab_order,
                  ...action.payload.response,
                }
          ),
        }
      );
    case ADMIN_CASE_REQUEST:
      return null;
    default:
      return state;
  }
};

export default reducer;
